import { buildWilderUrl, handleWilderError, WILDER_CONFIG } from './config.js';

/**
 * Busca todos os retirements de carbono da API Wilder
 * @returns {Promise<Array>} Array de retirements de carbono
 */
export const fetchCarbonRetirements = async () => {
  try {
    const response = await fetch(
      buildWilderUrl(WILDER_CONFIG.ENDPOINTS.RETIREMENTS),
      WILDER_CONFIG.REQUEST_CONFIG
    );
    
    if (!response.ok) {
      throw new Error(`Falha ao carregar os retirements: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.response.results || [];
  } catch (error) {
    throw new Error(handleWilderError(error, 'fetchCarbonRetirements'));
  }
};

/**
 * Filtra retirements por aposentadores que estão aposentando créditos da VERT
 * @param {Array} retirements - Array de retirements para filtrar
 * @returns {Array} Array filtrado de retirements
 */
export const filterRetirementsByVert = (retirements) => {
  if (!retirements || !Array.isArray(retirements)) return [];
  
  // IDs dos aposentadores que estão aposentando créditos da VERT
  const vertRetireeIds = [
    '1677772220839x902760234058070300', // VERT ECOTECH SA
    '1756291291041x952815036474707800', // Removall
    '1740073821427x499078341877695000'  // Agroessência
  ];
  
  return retirements.filter(retirement => 
    vertRetireeIds.includes(retirement.Retiree_ID) ||
    (typeof retirement.Retiree === 'string' && (
      /vert ecotech/i.test(retirement.Retiree) ||
      /removall/i.test(retirement.Retiree) ||
      /agroessência/i.test(retirement.Retiree) ||
      /agroessencia/i.test(retirement.Retiree)
    ))
  );
};

/**
 * Busca e filtra retirements de carbono da VERT
 * @returns {Promise<Array>} Array filtrado de retirements
 */
export const fetchFilteredCarbonRetirements = async () => {
  try {
    const allRetirements = await fetchCarbonRetirements();
    return filterRetirementsByVert(allRetirements);
  } catch (error) {
    throw new Error(handleWilderError(error, 'fetchFilteredCarbonRetirements'));
  }
};

/**
 * Formata data para exibição brasileira
 * @param {string} dateString - String de data para formatar
 * @returns {string} Data formatada ou '-' se inválida
 */
export const formatRetirementDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { dateStyle: 'medium' });
  } catch {
    return '-';
  }
};

/**
 * Formata número para exibição brasileira
 * @param {number|string} number - Número para formatar
 * @returns {string} Número formatado
 */
export const formatRetirementNumber = (number) => {
  if (!number && number !== 0) return '0';
  return new Intl.NumberFormat('pt-BR').format(number);
};

/**
 * Verifica se um retirement pertence à VERT
 * @param {Object} retirement - Objeto de retirement
 * @returns {boolean} True se pertencer à VERT
 */
export const isVertRetirement = (retirement) => {
  if (!retirement) return false;
  
  // IDs dos aposentadores que estão aposentando créditos da VERT
  const vertRetireeIds = [
    '1677772220839x902760234058070300', // VERT ECOTECH SA
    '1756291291041x952815036474707800', // Removall
    '1740073821427x499078341877695000'  // Agroessência
  ];
  
  return (
    vertRetireeIds.includes(retirement.Retiree_ID) ||
    (typeof retirement.Retiree === 'string' && (
      /vert ecotech/i.test(retirement.Retiree) ||
      /removall/i.test(retirement.Retiree) ||
      /agroessência/i.test(retirement.Retiree) ||
      /agroessencia/i.test(retirement.Retiree)
    ))
  );
};
